import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/wensity/badge";
import { Button } from "@/components/wensity/button";
import { ScriptActions } from "@/components/ScriptActions";
import { type ContentKind, formatDate, getContentBySlug } from "@/data/site";
import { useSiteContent } from "@/data/SiteContentContext";
import { NotFoundPage } from "@/pages/NotFoundPage";

const FULL_SCRIPT_ID = "full-script";

const labels: Record<ContentKind, { segment: string; title: string }> = {
  journal: { segment: "journal", title: "Journal" },
  guide: { segment: "guides", title: "Guides" },
  script: { segment: "scripts", title: "Scripts" },
};

function renderInline(text: string): ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part));
}

function renderBody(body: string) {
  const segments = body.split(/```([\s\S]*?)```/g);

  return segments.map((segment, segmentIndex) => {
    if (segmentIndex % 2 === 1) {
      // Fenced code blocks open with an optional language tag on their own
      // line (```powershell\n...) — strip it so it doesn't render as code.
      const code = segment.replace(/^[^\n]*\n?/, "").replace(/\n$/, "");
      return (
        <pre key={segmentIndex}>
          <code>{code}</code>
        </pre>
      );
    }

    const blocks: ReactNode[] = [];
    let listItems: ReactNode[] = [];

    const flushList = () => {
      if (!listItems.length) return;
      blocks.push(<ol key={`${segmentIndex}-list-${blocks.length}`}>{listItems}</ol>);
      listItems = [];
    };

    segment.split("\n").forEach((line, lineIndex) => {
      const numbered = line.match(/^\d+\.\s+(.*)/);
      if (numbered) {
        listItems.push(<li key={`${segmentIndex}-${lineIndex}`}>{renderInline(numbered[1])}</li>);
        return;
      }
      flushList();

      if (line.startsWith("## ")) {
        blocks.push(<h2 key={`${segmentIndex}-${lineIndex}`}>{renderInline(line.slice(3))}</h2>);
      } else if (line.startsWith("### ")) {
        blocks.push(<h3 key={`${segmentIndex}-${lineIndex}`}>{renderInline(line.slice(4))}</h3>);
      } else if (line.trim()) {
        blocks.push(<p key={`${segmentIndex}-${lineIndex}`}>{renderInline(line)}</p>);
      }
    });
    flushList();

    return blocks;
  });
}

export function ContentDetailPage({ kind }: { kind: ContentKind }) {
  const { slug = "" } = useParams();
  const { content } = useSiteContent();
  const item = getContentBySlug(content, kind, slug);
  const label = labels[kind];

  if (!item) {
    return <NotFoundPage />;
  }

  return (
    <article className="mx-auto max-w-3xl space-y-8">
      <div className="space-y-4">
        <Button asChild variant="ghost" className="px-0 text-[var(--color-muted)] hover:text-[var(--color-foreground)]">
          <Link to={`/${label.segment}`}>← Back to {label.title}</Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--color-muted)]">
          <span className="uppercase tracking-[0.14em]">{item.kind}</span>
          <time dateTime={item.date}>{formatDate(item.date)}</time>
        </div>
        <h1 className="text-4xl font-semibold tracking-tight text-[var(--color-foreground)]">{item.title}</h1>
        <p className="text-lg text-[var(--color-muted)]">{item.description}</p>
        <div className="flex flex-wrap gap-2">
          {item.tags.map((tag) => (
            <Badge key={tag} variant="neutral" className="border-[var(--color-border)] bg-white/5 text-[var(--color-muted)]">
              {tag}
            </Badge>
          ))}
        </div>
      </div>
      {item.kind === "script" ? <ScriptActions item={item} fullScriptId={FULL_SCRIPT_ID} /> : null}
      <div id={FULL_SCRIPT_ID} className="prose-site border-t border-[var(--color-border)] pt-8">
        {renderBody(item.body)}
      </div>
    </article>
  );
}
